/**
 * Staff Document Service — API client for RubyTill INTERNAL STAFF document
 * management (staff-service backend, tenant-less).
 *
 * Port of platform-app services/documentService.ts with:
 *   - base URL   → staffHost()  (shared `platform` cluster, /staff base path)
 *   - auth       → staffAuthHeader()  (bare token from localStorage 'jwtToken')
 *   - paths      → /admin/... and /me/... (no /tenant/{tid} prefix anywhere)
 * Response body shapes are the SAME as users-service, so the unwrapping /
 * normalization logic is preserved verbatim.
 */

import { staffHost, staffAuthHeader } from './staffApi';

// =============================================================================
// REQUEST HELPER (preserves documentService's unwrapping + error handling)
// =============================================================================

const request = async (path: string, options: RequestInit = {}): Promise<any> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...staffAuthHeader(),
    ...(options.headers as Record<string, string> || {}),
  };

  const response = await fetch(`${staffHost()}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || `Request failed: ${response.status}`);
  }

  const data = await response.json().catch(() => ({}));

  // Handle API Gateway style responses where payload may be in `body` as a string
  if (typeof data.body === 'string') {
    try {
      return JSON.parse(data.body);
    } catch {
      return data.body;
    }
  }

  return data;
};

// =============================================================================
// TYPES (ported from documentService; tenantId is optional — staff-service is
// tenant-less but response bodies keep the users-service shape)
// =============================================================================

export type DocumentCategory = 'id' | 'tax' | 'legal' | 'certification' | 'application' | 'financial' | 'onboarding' | 'compliance' | 'other';
export type DocumentRequestStatus = 'pending' | 'submitted' | 'verified' | 'rejected';
export type DocumentStatus = 'active' | 'expired' | 'superseded';
export type DocumentTemplateType = 'upload' | 'acknowledgment' | 'form' | 'sheet' | 'fillable_doc';

export interface FormField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'date' | 'email' | 'phone';
  required: boolean;
  options?: string[]; // For select type
  placeholder?: string;
  order: number;
}

export interface DocumentTemplate {
  tenantId?: string;
  id: string;
  name: string;
  description?: string;
  category: DocumentCategory;
  isRequired: boolean;
  hasExpiry: boolean;
  acceptedTypes: string[]; // e.g., ['pdf', 'jpg', 'png']
  // Template type fields
  templateType?: DocumentTemplateType;
  disclaimerText?: string; // For acknowledgment type
  formFields?: FormField[]; // For form type
  referenceFileKey?: string; // For fillable_doc type
  referenceFileName?: string; // For fillable_doc type
  createdAt: number;
  updatedAt: number;
}

export interface OnboardingTemplate {
  tenantId?: string;
  id: string;
  name: string;
  description?: string;
  documents: { templateId: string; dueDays: number; order: number }[];
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface DocumentRequest {
  tenantId?: string;
  id: string;
  userId: string;
  documentTemplateId: string;
  templateName: string;
  category: DocumentCategory;
  status: DocumentRequestStatus;
  onboardingTemplateId?: string;
  assignmentId?: string;
  requestedAt: number;
  submittedAt?: number;
  verifiedAt?: number;
  rejectedAt?: number;
  rejectionReason?: string;
  verifiedBy?: string;
  documentId?: string;
  dueDate?: number;
  notes?: string;
  // Template type fields
  templateType?: DocumentTemplateType;
  // Template file constraints (populated from template)
  allowedFileTypes?: string[];
  maxFileSize?: number;
  requiresExpiry?: boolean;
  // For acknowledgment type
  acknowledgedAt?: number;
  acknowledgedBy?: string;
  signature?: string;
  // For form type
  formResponses?: Record<string, any>;
}

export interface StaffDocument {
  tenantId?: string;
  id: string;
  userId: string;
  // Optional: admin-added documents are standalone (no request/template).
  requestId?: string;
  documentTemplateId?: string;
  // Optional friendly label for admin-added documents.
  documentName?: string;
  fileName: string;
  fileKey: string;
  fileType: string;
  fileSize: number;
  expiryDate?: number;
  uploadedAt: number;
  status: DocumentStatus;
}

/** Alias kept so ports from platform-app stay mechanical. */
export type EmployeeDocument = StaffDocument;

export interface WorkflowStatus {
  id: string;
  templateId: string;  // The onboarding template ID for filtering requests
  name: string;
  progress: number;
  totalDocuments: number;
  statusCounts: {
    pending: number;
    submitted: number;
    verified: number;
    rejected: number;
  };
  requests: DocumentRequest[];
}

/**
 * A workflow grouped by assignment, as returned by getOnboardingStatus.
 * Each entry represents one assigned onboarding template (round) with its
 * per-step document requests rolled up.
 */
export interface OnboardingWorkflowGroup {
  assignmentId?: string;
  onboardingTemplateId: string;
  name: string;
  assignedAt?: number;
  progress: number;          // 0-100
  statusCounts: { pending: number; submitted: number; verified: number; rejected: number };
  steps: Array<{
    requestId: string;
    templateName: string;
    templateType?: string;
    status: 'pending' | 'submitted' | 'verified' | 'rejected';
    dueDate?: number;
    submittedAt?: number;
    verifiedAt?: number;
    rejectionReason?: string;
    submittedByAdmin?: boolean;
    documentTemplateId?: string;
    formFields?: any[];
    resultFileKey?: string;
    resultFileName?: string;
  }>;
}

export interface OnboardingStatus {
  status: 'not_started' | 'in_progress' | 'completed';
  templateName?: string;
  templateId?: string;
  startedAt?: number;
  completedAt?: number;
  progress: number; // 0-100
  totalDocuments: number;
  statusCounts?: {
    pending: number;
    submitted: number;
    verified: number;
    rejected: number;
  };
  requests: DocumentRequest[];
  message?: string;
  // Multi-workflow support — workflows grouped by assignment
  workflows?: OnboardingWorkflowGroup[];
  activeWorkflowIds?: string[];
}

// =============================================================================
// DOCUMENT TEMPLATES (Admin)
// =============================================================================

export const staffDocumentTemplates = {
  /** Get all document templates. */
  getAll: async (): Promise<DocumentTemplate[]> => {
    try {
      const response = await request('/admin/document-templates');
      // Handle various response formats (same normalization as documentService)
      if (Array.isArray(response)) return response;
      if (response?.Items && Array.isArray(response.Items)) return response.Items;
      if (response?.templates && Array.isArray(response.templates)) return response.templates;
      return [];
    } catch (err) {
      console.error('Error fetching document templates:', err);
      return [];
    }
  },

  /** Create a new document template. */
  create: async (
    data: Omit<DocumentTemplate, 'tenantId' | 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<DocumentTemplate> => {
    return request('/admin/document-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Update a document template. */
  update: async (
    templateId: string,
    data: Partial<Omit<DocumentTemplate, 'tenantId' | 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<DocumentTemplate> => {
    return request(`/admin/document-templates/${encodeURIComponent(templateId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /** Delete a document template. */
  delete: async (templateId: string): Promise<void> => {
    return request(`/admin/document-templates/${encodeURIComponent(templateId)}`, {
      method: 'DELETE',
    });
  },
};

// =============================================================================
// ONBOARDING TEMPLATES (Admin)
// =============================================================================

export const staffOnboardingTemplates = {
  /** Get all onboarding templates. */
  getAll: async (): Promise<OnboardingTemplate[]> => {
    try {
      const response = await request('/admin/onboarding-templates');
      if (Array.isArray(response)) return response;
      if (response?.Items && Array.isArray(response.Items)) return response.Items;
      if (response?.templates && Array.isArray(response.templates)) return response.templates;
      return [];
    } catch (err) {
      console.error('Error fetching onboarding templates:', err);
      return [];
    }
  },

  /** Create a new onboarding template. */
  create: async (
    data: Omit<OnboardingTemplate, 'tenantId' | 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<OnboardingTemplate> => {
    return request('/admin/onboarding-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Update an onboarding template. */
  update: async (
    templateId: string,
    data: Partial<Omit<OnboardingTemplate, 'tenantId' | 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<OnboardingTemplate> => {
    return request(`/admin/onboarding-templates/${encodeURIComponent(templateId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /** Delete an onboarding template. */
  delete: async (templateId: string): Promise<void> => {
    return request(`/admin/onboarding-templates/${encodeURIComponent(templateId)}`, {
      method: 'DELETE',
    });
  },
};

// =============================================================================
// DOCUMENT REQUESTS (Admin)
// =============================================================================

export const staffDocumentRequests = {
  /** Get document requests (optionally filtered by staff member or status). */
  getAll: async (
    params?: { userId?: string; status?: DocumentRequestStatus }
  ): Promise<DocumentRequest[]> => {
    const qs = new URLSearchParams();
    if (params?.userId) qs.append('userId', params.userId);
    if (params?.status) qs.append('status', params.status);
    const queryString = qs.toString();
    const path = `/admin/document-requests${queryString ? `?${queryString}` : ''}`;

    try {
      const response = await request(path);
      if (Array.isArray(response)) return response;
      if (response?.requests && Array.isArray(response.requests)) return response.requests;
      return response || [];
    } catch (err) {
      console.error('Error fetching document requests:', err);
      return [];
    }
  },

  /** Create a document request for a specific staff member. */
  create: async (
    data: {
      userId: string;
      documentTemplateId: string;
      dueDate?: number;
      notes?: string;
      onboardingTemplateId?: string;
    }
  ): Promise<DocumentRequest> => {
    return request('/admin/document-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Start onboarding for a staff member (creates all document requests from template). */
  startOnboarding: async (
    staffId: string,
    onboardingTemplateId: string
  ): Promise<{ message: string; requests: DocumentRequest[] }> => {
    return request(`/admin/staff/${encodeURIComponent(staffId)}/start-onboarding`, {
      method: 'POST',
      body: JSON.stringify({ onboardingTemplateId }),
    });
  },

  /**
   * Remove/cancel onboarding for a staff member (removes pending requests).
   * @param workflowId Optional: remove a specific workflow, otherwise removes all.
   */
  removeOnboarding: async (
    staffId: string,
    workflowId?: string
  ): Promise<{ message: string; removedWorkflows: string[]; remainingWorkflows: string[] }> => {
    const url = workflowId
      ? `/admin/staff/${encodeURIComponent(staffId)}/onboarding?workflowId=${encodeURIComponent(workflowId)}`
      : `/admin/staff/${encodeURIComponent(staffId)}/onboarding`;
    return request(url, {
      method: 'DELETE',
    });
  },

  /**
   * Verify or reject a document submission.
   * @param action 'verify' | 'reject' (the backend's expected action verb)
   */
  verify: async (
    requestId: string,
    action: 'verify' | 'reject',
    reason?: string,
    verifiedBy?: string
  ): Promise<DocumentRequest> => {
    return request(`/admin/document-requests/${encodeURIComponent(requestId)}/verify`, {
      method: 'PUT',
      body: JSON.stringify({ action, reason, verifiedBy }),
    });
  },

  /** Update a document request's status directly (admin only). */
  updateStatus: async (
    requestId: string,
    status: DocumentRequestStatus
  ): Promise<{ success: boolean; message: string; request: DocumentRequest }> => {
    return request(`/admin/document-requests/${encodeURIComponent(requestId)}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },

  /** Delete/cancel a document request. */
  delete: async (requestId: string): Promise<void> => {
    return request(`/admin/document-requests/${encodeURIComponent(requestId)}`, {
      method: 'DELETE',
    });
  },

  /** Send a reminder email for pending documents. */
  sendReminder: async (
    staffId: string,
    body?: { requestIds?: string[]; message?: string }
  ): Promise<{ message: string }> => {
    return request(`/admin/staff/${encodeURIComponent(staffId)}/send-document-reminder`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  },

  /** Re-request a document (after deletion or rejection). */
  reRequest: async (
    body: {
      userId: string;
      documentTemplateId: string;
      previousRequestId?: string;
      message?: string;
      dueDate?: number;
    }
  ): Promise<DocumentRequest> => {
    return request('/admin/document-requests/re-request', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** Admin records a step on the staff member's behalf (form / upload / acknowledge). */
  submitOnBehalf: (requestId: string, body: {
    method: 'form' | 'upload' | 'acknowledge';
    responses?: Record<string, any>;
    fileKey?: string; fileName?: string; fileType?: string; fileSize?: number;
    note?: string;
  }) => request(`/admin/document-requests/${encodeURIComponent(requestId)}/submit-on-behalf`, {
    method: 'POST', body: JSON.stringify(body),
  }),

  /** Admin attaches a result file (e.g. background check) to a request. */
  attachResult: (requestId: string, body: {
    fileKey: string; fileName: string; fileType?: string; fileSize?: number;
  }) => request(`/admin/document-requests/${encodeURIComponent(requestId)}/attach-result`, {
    method: 'POST', body: JSON.stringify(body),
  }),
};

// =============================================================================
// STAFF MEMBER DOCUMENTS (Admin view of one staff member)
// =============================================================================

export const staffMemberDocuments = {
  /** Get all documents + requests for a specific staff member. */
  getByUser: async (
    staffId: string
  ): Promise<{ documents: StaffDocument[]; requests: DocumentRequest[] }> => {
    try {
      const response = await request(`/admin/staff/${encodeURIComponent(staffId)}/documents`);
      return response || { documents: [], requests: [] };
    } catch (err) {
      console.error('Error fetching staff documents:', err);
      return { documents: [], requests: [] };
    }
  },

  /** Get onboarding status (workflows + per-step requests) for a staff member. */
  getOnboardingStatus: async (staffId: string): Promise<OnboardingStatus> => {
    return request(`/admin/staff/${encodeURIComponent(staffId)}/onboarding-status`);
  },

  /**
   * Admin adds a standalone document directly to a staff member (no
   * request/template). Omitting requestId triggers the standalone path.
   */
  addByAdmin: async (
    staffId: string,
    data: {
      fileName: string;
      fileKey: string;
      fileType: string;
      fileSize: number;
      documentName?: string;
      expiryDate?: number;
    }
  ): Promise<{ document: StaffDocument }> => {
    return request(`/admin/staff/${encodeURIComponent(staffId)}/documents`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// =============================================================================
// STAFF PORTAL (self-service — the signed-in staff member's own documents)
// =============================================================================

export const staffPortal = {
  /**
   * Get my documents + document requests.
   * Same body shape as staffMemberDocuments.getByUser: { documents, requests }.
   */
  getMyDocuments: async (): Promise<{ documents: StaffDocument[]; requests: DocumentRequest[] }> => {
    try {
      const response = await request('/me/documents');
      return response || { documents: [], requests: [] };
    } catch (err) {
      console.error('Error fetching my documents:', err);
      return { documents: [], requests: [] };
    }
  },

  /** Get my onboarding status (workflows + per-step requests). */
  getMyOnboardingStatus: async (): Promise<OnboardingStatus> => {
    return request('/me/onboarding-status');
  },

  /**
   * Submit a document (for a request when requestId is given; standalone
   * uploads omit it).
   */
  submitDocument: async (
    data: {
      requestId?: string;
      fileName: string;
      fileKey: string;
      fileType: string;
      fileSize: number;
      expiryDate?: number;
    }
  ): Promise<StaffDocument> => {
    return request('/me/documents', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Acknowledge a disclaimer/agreement (acknowledgment type templates). */
  acknowledge: async (
    requestId: string,
    data?: { signature?: string }
  ): Promise<{ success: boolean; message: string; request: DocumentRequest }> => {
    return request(`/me/document-requests/${encodeURIComponent(requestId)}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  },

  /** Submit form responses (form type templates). */
  submitForm: async (
    requestId: string,
    responses: Record<string, any>
  ): Promise<{ success: boolean; message: string; request: DocumentRequest }> => {
    return request(`/me/document-requests/${encodeURIComponent(requestId)}/submit-form`, {
      method: 'POST',
      body: JSON.stringify({ responses }),
    });
  },
};

// =============================================================================
// DOCUMENT UPLOADS (staff-service /uploads — NOT the tenant uploads-service)
// =============================================================================

export const staffDocumentUploads = {
  /**
   * Get a presigned URL for uploading a document.
   * Managers may pass staffId to sign on someone's behalf; everyone else
   * signs their own.
   */
  getUploadUrl: async (
    fileName: string,
    contentType: string,
    staffId?: string
  ): Promise<{ uploadUrl: string; key: string; fileName: string }> => {
    return request('/uploads/sign', {
      method: 'POST',
      body: JSON.stringify({ fileName, contentType, ...(staffId ? { staffId } : {}) }),
    });
  },

  /** Upload a file to S3 using a presigned URL (plain PUT, never via API GW). */
  uploadFile: async (uploadUrl: string, file: File | Blob, contentType: string): Promise<void> => {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to upload file');
    }
  },

  /** Get a presigned URL for downloading a document (attachment disposition). */
  getDownloadUrl: async (key: string, fileName?: string): Promise<{ url: string }> => {
    return request('/uploads/download', {
      method: 'POST',
      body: JSON.stringify({ key, disposition: 'attachment', ...(fileName ? { fileName } : {}) }),
    });
  },

  /** Get a presigned URL for viewing a document inline. */
  getViewUrl: async (key: string, fileName?: string): Promise<{ url: string }> => {
    return request('/uploads/download', {
      method: 'POST',
      body: JSON.stringify({ key, disposition: 'inline', ...(fileName ? { fileName } : {}) }),
    });
  },

  // NOTE: staff-service has no delete endpoint for uploaded objects (omitted
  // deliberately — see backend route map).
};

// =============================================================================
// HELPER FUNCTIONS (ported; STATUS_COLORS restyled for the dark admin chrome)
// =============================================================================

export const CATEGORY_LABELS: Record<DocumentCategory, string> = {
  id: 'Identification',
  tax: 'Tax Forms',
  legal: 'Legal',
  certification: 'Certifications',
  application: 'Application',
  financial: 'Financial',
  onboarding: 'Onboarding',
  compliance: 'Compliance',
  other: 'Other',
};

export const STATUS_LABELS: Record<DocumentRequestStatus, string> = {
  pending: 'Pending',
  submitted: 'Submitted',
  verified: 'Verified',
  rejected: 'Rejected',
};

/** Translucent badge colors that read on bg-dark-800/900 surfaces. */
export const STATUS_COLORS: Record<DocumentRequestStatus, { bg: string; text: string }> = {
  pending: { bg: 'bg-amber-500/15', text: 'text-amber-300' },
  submitted: { bg: 'bg-blue-500/15', text: 'text-blue-300' },
  verified: { bg: 'bg-emerald-500/15', text: 'text-emerald-300' },
  rejected: { bg: 'bg-red-500/15', text: 'text-red-300' },
};

/** Format a file size in bytes to a human-readable string. */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/** Check if a file type is allowed for a template. */
export const isFileTypeAllowed = (
  fileType: string,
  acceptedTypes: string[]
): boolean => {
  const extension = fileType.split('/')[1]?.toLowerCase();
  const mimeType = fileType.toLowerCase();

  return acceptedTypes.some(type => {
    const lowerType = type.toLowerCase();
    return (
      lowerType === extension ||
      lowerType === mimeType ||
      (lowerType === 'image' && mimeType.startsWith('image/')) ||
      (lowerType === 'pdf' && mimeType === 'application/pdf')
    );
  });
};

/** Get accepted file types as MIME types for input accept attribute. */
export const getAcceptAttribute = (acceptedTypes?: string[] | null): string => {
  // Default to common document types if not specified
  if (!acceptedTypes || acceptedTypes.length === 0) {
    return 'application/pdf,image/jpeg,image/png,image/gif';
  }

  const mimeMap: Record<string, string> = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    image: 'image/*',
  };

  return acceptedTypes
    .map(type => mimeMap[type.toLowerCase()] || type)
    .join(',');
};
